import { ApiClientError, buildApiUrl, getApiErrorMessage, parseJson } from '@/lib/api/client';
import type { ApiErrorResponse } from '@/lib/auth/types';

const DEFAULT_NETWORK_ERROR_MESSAGE = 'No pudimos validar el numero. Intenta nuevamente.';

export interface AppointmentOtpClient {
  id: string | null;
  isRegistered: boolean;
  name: string | null;
  phone: string;
  requiresName: boolean;
}

interface AppointmentOtpResponse extends ApiErrorResponse {
  challengeId?: string;
  client?: AppointmentOtpClient;
  expiresInSeconds?: number;
}

function getAppointmentOtpEndpoint(path: 'request' | 'verify'): string {
  const configuredEndpoint = process.env.NEXT_PUBLIC_APPOINTMENT_OTP_ENDPOINT?.trim();

  if (configuredEndpoint) {
    return buildApiUrl(`${configuredEndpoint.replace(/\/+$/, '')}/${path}`);
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim();

  if (supabaseUrl) {
    return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/appointment-otp/${path}`;
  }

  throw new ApiClientError(
    'Configura NEXT_PUBLIC_APPOINTMENT_OTP_ENDPOINT o NEXT_PUBLIC_SUPABASE_URL para confirmar citas.'
  );
}

function isJwtLike(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts[0].startsWith('eyJ') && parts.every((part) => part.length > 0);
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

  if (supabaseAnonKey) {
    headers.apikey = supabaseAnonKey;
    if (isJwtLike(supabaseAnonKey)) {
      headers.Authorization = `Bearer ${supabaseAnonKey}`;
    }
  }

  return headers;
}

function assertOk(response: Response, payload: AppointmentOtpResponse | null, fallbackMessage: string) {
  if (!response.ok || payload?.ok === false || payload?.success === false) {
    const message = getApiErrorMessage(payload) ?? fallbackMessage;
    throw new ApiClientError(message, response.status);
  }
}

export async function requestAppointmentOtp({
  businessId,
  phone,
  professionalId,
  serviceId,
  startAt,
}: {
  businessId: string;
  phone: string;
  professionalId: string;
  serviceId: string;
  startAt: string;
}): Promise<{ challengeId: string; expiresInSeconds: number }> {
  try {
    const response = await fetch(getAppointmentOtpEndpoint('request'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        business_id: businessId,
        phone,
        professional_id: professionalId,
        service_id: serviceId,
        start_at: startAt,
      }),
    });

    const payload = await parseJson<AppointmentOtpResponse>(response);
    assertOk(response, payload, 'No fue posible enviar el codigo por WhatsApp.');

    if (!payload?.challengeId) {
      throw new ApiClientError('El servidor no devolvio un challenge OTP.');
    }

    return {
      challengeId: payload.challengeId,
      expiresInSeconds: payload.expiresInSeconds ?? 300,
    };
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }

    throw new ApiClientError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}

export async function verifyAppointmentOtp({
  challengeId,
  otp,
  phone,
}: {
  challengeId: string;
  otp: string;
  phone: string;
}): Promise<AppointmentOtpClient> {
  try {
    const response = await fetch(getAppointmentOtpEndpoint('verify'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        challengeId,
        otp,
        phone,
      }),
    });

    const payload = await parseJson<AppointmentOtpResponse>(response);
    assertOk(response, payload, 'Codigo invalido o expirado.');

    if (!payload?.client) {
      throw new ApiClientError('No fue posible resolver el cliente de la cita.');
    }

    return payload.client;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }

    throw new ApiClientError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}
